import { supabase } from '@/lib/supabase';
import type { UserRole } from '@/lib/types/database';
import type {
  BulkCreateUser,
  BulkCreateResult,
} from '@/lib/types/bulk-import.types';

export interface InviteUserParams {
  email: string;
  role: UserRole;
  fullName?: string;
  groupImportKeys?: string[];
}

async function getFunctionErrorMessage(
  error: unknown,
  fallback: string
): Promise<string> {
  const context =
    typeof error === 'object' && error && 'context' in error
      ? (error as { context?: Response }).context
      : undefined;

  if (!context) {
    return fallback;
  }

  try {
    const body = (await context.clone().json()) as { error?: unknown };
    return typeof body.error === 'string' ? body.error : fallback;
  } catch {
    return fallback;
  }
}

export async function inviteUser({
  email,
  role,
  fullName,
  groupImportKeys,
}: InviteUserParams) {
  const { data, error } = await supabase.functions.invoke('invite-user', {
    body: { email, role, fullName, groupImportKeys },
  });

  if (error) {
    throw new Error(await getFunctionErrorMessage(error, error.message));
  }

  return data;
}

export async function bulkCreateUsers(
  users: BulkCreateUser[]
): Promise<BulkCreateResult> {
  const { data, error } = await supabase.functions.invoke('bulk-create-users', {
    body: { users },
  });

  if (error) {
    throw new Error(await getFunctionErrorMessage(error, error.message));
  }

  return data as BulkCreateResult;
}
